import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, FileText, Lock, User, Download, FilePlus2, FileCheck2, UserCheck, Loader2, MessageSquare, CircleDot, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError, openDocument } from "../lib/api";
import { STATUSES, ASSIGNEES, STATUS_COLORS, TYPE_LABELS, TYPE_COLORS, fmtDate, fmtDateTime } from "./constants";

const DOC_KIND_LABELS = { identita: "Documento d'identità (fronte e retro)", tessera_sanitaria: "Tessera sanitaria" };

export default function AdminRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/requests/${id}`).then((r) => setReq(r.data)).catch(() => setReq(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const updateField = async (field, value) => {
    try {
      const { data } = await api.patch(`/admin/requests/${id}`, { [field]: value });
      setReq((prev) => ({ ...prev, ...data }));
      toast.success(field === "status" ? "Stato aggiornato" : "Assegnazione aggiornata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    try {
      const { data } = await api.post(`/admin/requests/${id}/messages`, { body: reply });
      setReq((prev) => ({ ...prev, messages: [...prev.messages, data] }));
      setReply("");
      toast.success("Messaggio inviato al cliente");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/admin/requests/${id}/notes`, { body: note });
      setReq((prev) => ({ ...prev, internal_notes: [...(prev.internal_notes || []), data] }));
      setNote("");
      toast.success("Nota interna aggiunta");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    }
  };

  if (req === null) return <div className="py-20 text-center text-slate-400">Caricamento...</div>;
  if (req === false) return <div className="py-20 text-center text-slate-500">Richiesta non trovata.</div>;

  return (
    <div data-testid="admin-request-detail-page" className="space-y-6">
      <button data-testid="admin-detail-back" onClick={() => navigate("/admin/richieste")} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0A192F]">
        <ArrowLeft className="h-4 w-4" /> Torna all'elenco
      </button>

      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full px-3 py-1 text-xs font-bold text-white" style={{ backgroundColor: TYPE_COLORS[req.type] || "#0A192F" }}>
          {req.category || TYPE_LABELS[req.type] || req.type}
        </span>
        <h1 className="font-display text-2xl font-bold text-[#0A192F]">{req.title}</h1>
        {req.price != null && <span className="rounded-full bg-[#0A192F] px-3 py-1 text-xs font-bold text-white">€{req.price}</span>}
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[req.status] || "bg-slate-100 text-slate-600"}`}>{req.status}</span>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* LEFT: info + messages */}
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Dettaglio richiesta</h3>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><dt className="text-xs uppercase tracking-wide text-slate-400">Categoria</dt><dd className="font-semibold text-[#0A192F]">{req.subcategory}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-slate-400">Data apertura</dt><dd className="font-semibold text-[#0A192F]">{fmtDate(req.created_at)}</dd></div>
            </dl>
            <div className="mt-4">
              <dt className="text-xs uppercase tracking-wide text-slate-400">Descrizione</dt>
              <dd className="mt-1 text-slate-700">{req.description || <span className="text-slate-400">Nessuna descrizione</span>}</dd>
            </div>
          </div>

          {/* Messages / conversation */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Conversazione con il cliente</h3>
            <div className="mt-4 space-y-3">
              {(req.messages || []).length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Nessun messaggio.</p>
              ) : (
                req.messages.map((m) => (
                  <div key={m.id} data-testid={`admin-message-${m.id}`} className={`flex ${m.from_staff ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.from_staff ? "bg-[#10B981] text-white" : "bg-slate-100 text-slate-800"}`}>
                      <p>{m.body}</p>
                      <p className={`mt-1 text-[10px] ${m.from_staff ? "text-white/70" : "text-slate-400"}`}>
                        {m.from_staff ? "Staff" : req.client?.name || "Cliente"} · {fmtDateTime(m.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={sendReply} className="mt-4 flex items-center gap-3">
              <input data-testid="admin-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Scrivi al cliente..." className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#10B981]" />
              <button data-testid="admin-reply-send" className="flex h-10 w-10 items-center justify-center rounded-full bg-[#10B981] text-white transition-colors hover:bg-[#059669]"><Send className="h-5 w-5" /></button>
            </form>
          </div>

          {/* Timeline */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Cronologia</h3>
            {(req.events || []).length === 0 ? (
              <p className="mt-3 text-sm text-slate-400">Nessun evento.</p>
            ) : (
              <ol className="relative mt-6 border-l-2 border-slate-100">
                {[...(req.events || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((ev) => {
                  const Icon = { created: FilePlus2, document: FileCheck2, assigned: UserCheck, status: Loader2, reply: MessageSquare }[ev.type] || CircleDot;
                  return (
                    <li key={ev.id} data-testid={`admin-event-${ev.id}`} className="mb-6 ml-6 last:mb-0">
                      <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-[#0A192F] ring-4 ring-white">
                        <Icon className="h-3.5 w-3.5 text-white" />
                      </span>
                      <p className="font-semibold text-[#0A192F]">{ev.label}</p>
                      {ev.description && <p className="text-sm text-slate-600">{ev.description}</p>}
                      <p className="mt-0.5 text-xs text-slate-400">{fmtDateTime(ev.created_at)}</p>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {/* Documents */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Documenti</h3>
            {req.required_docs && (
              <div data-testid="admin-required-docs" className="mt-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Documenti richiesti</p>
                {req.required_docs.map((k) => {
                  const doc = (req.documents || []).find((d) => d.doc_kind === k);
                  return (
                    <div key={k} data-testid={`admin-reqdoc-${k}`} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${doc ? "border-[#10B981]/30 bg-[#10B981]/5" : "border-[#F97316]/40 bg-[#F97316]/5"}`}>
                      <div className="flex items-center gap-2 text-sm">
                        {doc ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#10B981]" /> : <AlertCircle className="h-5 w-5 flex-shrink-0 text-[#F97316]" />}
                        <span className="font-medium text-[#0A192F]">{DOC_KIND_LABELS[k] || k}</span>
                      </div>
                      {doc ? (
                        <button data-testid={`admin-reqdoc-open-${k}`} onClick={() => openDocument(doc.id)} className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-[#0A192F] hover:border-[#10B981]">
                          Apri <Download className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <span className="flex-shrink-0 rounded-full bg-[#F97316]/15 px-3 py-1 text-xs font-bold text-[#F97316]">Mancante</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {(req.documents || []).length === 0 ? (
              <p className="mt-3 text-sm text-slate-400">Nessun documento collegato.</p>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {req.documents.map((d) => (
                  <button key={d.id} data-testid={`admin-doc-${d.id}`} onClick={() => openDocument(d.id)} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-[#10B981]">
                    <FileText className="h-5 w-5 flex-shrink-0 text-[#10B981]" />
                    <div className="flex-1 overflow-hidden"><p className="truncate font-semibold text-[#0A192F]">{d.name}</p><p className="mt-1 text-xs text-slate-400">{d.doc_kind ? `${DOC_KIND_LABELS[d.doc_kind] || d.doc_kind} · ` : ""}{fmtDate(d.created_at)}</p></div>
                    <Download className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: client, actions, internal notes */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Cliente</h3>
            <div className="mt-4 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0A192F] font-display text-sm font-bold text-white">
                <User className="h-5 w-5" />
              </span>
              <div>
                <p className="font-semibold text-[#0A192F]" data-testid="admin-detail-client-name">{req.client?.name}</p>
                <p className="text-sm text-slate-500">{req.client?.email}</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-400">Piano: {req.client?.subscription?.active ? "Attivo" : "Non attivo"}</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-[#0A192F]">Azioni</h3>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-500">Stato</label>
            <select data-testid="admin-status-select" value={req.status} onChange={(e) => updateField("status", e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#10B981]">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-500">Assegna a</label>
            <select data-testid="admin-assign-select" value={req.assigned_to || ""} onChange={(e) => updateField("assigned_to", e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#10B981]">
              <option value="" disabled>Seleziona reparto/persona</option>
              {ASSIGNEES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div className="rounded-2xl border border-[#F97316]/30 bg-[#F97316]/5 p-6">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-[#F97316]" />
              <h3 className="font-display text-lg font-bold text-[#0A192F]">Note interne</h3>
            </div>
            <p className="mt-1 text-xs text-slate-500">Visibili solo allo staff, mai al cliente.</p>
            <div className="mt-4 space-y-3">
              {(req.internal_notes || []).length === 0 ? (
                <p className="text-sm text-slate-400">Nessuna nota.</p>
              ) : (
                req.internal_notes.map((n) => (
                  <div key={n.id} data-testid={`admin-note-${n.id}`} className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
                    <p className="text-slate-700">{n.body}</p>
                    <p className="mt-1 text-[10px] text-slate-400">{n.author} · {fmtDateTime(n.created_at)}</p>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={addNote} className="mt-4 space-y-2">
              <textarea data-testid="admin-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi una nota interna..." className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F97316]" />
              <button data-testid="admin-note-submit" className="w-full rounded-full bg-[#0A192F] px-4 py-2.5 text-sm font-semibold text-white">Aggiungi nota</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
